import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FieldErrors } from 'react-hook-form'
import ControlledSelect, { IControlledSelectProps } from 'transfix-form/components/ControlledSelect'
import { Box } from 'transfix-ui/core'
import { IDriver } from 'apps/fleet-planner/types'
import { Radio } from 'transfix-ui'
import { TRateConfirmationFormSchema } from './components/RateConfirmationForm/schema/RateConfirmationFormSchema'
import { TInputRefType } from './components/RateConfirmationForm/types'

export interface IDriverOptions extends Pick<IControlledSelectProps, 'control' | 'SelectProps'> {
  drivers: IDriver[]
  formErrors: FieldErrors<TRateConfirmationFormSchema>
  setDefaultDriver: (driverId: number) => void
  setDriverIsRequired: (required: boolean) => void
  inputRef?: TInputRefType['driverId']
}

export const DriverOptions: FC<IDriverOptions> = ({
  control,
  formErrors,
  drivers = [],
  setDefaultDriver,
  setDriverIsRequired,
  inputRef,
}) => {
  const [showDriver, setShowDriver] = useState(false)

  const driversOptions = useMemo(
    () =>
      drivers.map(d => ({
        value: d.transfixUserId!,
        label: d.name || '',
      })),
    [drivers]
  )

  /**
   * In the case where there is only 1 driver, set the driverId automatically
   */
  useEffect(() => {
    if (driversOptions?.length <= 1 && showDriver) {
      const [option] = driversOptions || []

      if (option) {
        setDefaultDriver(option.value)
      }
    }
  }, [setDefaultDriver, driversOptions, showDriver, drivers])
  const changeHandler = useCallback((value: boolean) => () => setShowDriver(value), [setShowDriver])

  useEffect(() => {
    setDriverIsRequired(showDriver)
  }, [setDriverIsRequired, showDriver])

  return (
    <>
      <Box component='legend' className='label required' fontSize='0.875rem !important'>
        Are you ready to assign a driver?
      </Box>
      <Radio
        id='show-driver'
        checked={showDriver}
        dataTest='show-driver'
        label='Yes, I’m ready to assign a driver'
        value='show-driver'
        onChange={changeHandler(true)}
      />
      <Radio
        id='hide-driver'
        checked={!showDriver}
        dataTest='hide-driver'
        label='No, I’ll assign a driver later'
        value='hide-driver'
        onChange={changeHandler(false)}
      />
      {showDriver && (
        <Box mt={2}>
          <ControlledSelect
            control={control}
            name='driverId'
            SelectProps={{
              large: true,
              options: driversOptions,
              label: 'Select a driver',
              required: true,
              error: !!formErrors?.driverId?.message,
              validationMessage: formErrors?.driverId?.message,
              inputRef,
              dataTest: 'driver-selector',
            }}
          />
        </Box>
      )}
      <Controller name='driverRequired' as={<input type='hidden' />} />
    </>
  )
}
